import { distanceMeters } from "../shared/time";

export interface ShuttleStopFix {
	lat: number;
	lon: number;
	accuracyMeters: number;
	fixAtMs: number;
}

export interface ShuttleStopDwell {
	enteredAtMs: number | null;
	lastFixAtMs: number | null;
	consecutiveFixes: number;
}

/** Median of the stationary fixes on three observed afternoons. */
export const SHUTTLE_STOP = { lat: 37.848704, lon: -122.24016 };
/** 40 m diameter: the observed waiting clusters fit with their full accuracy circles inside. */
export const SHUTTLE_STOP_RADIUS_METERS = 20;
export const SHUTTLE_STOP_DWELL_MS = 30_000;
export const SHUTTLE_STOP_REQUIRED_FIXES = 3;
/** Native fixes normally arrive every 15–19 seconds; a larger gap is not continuous evidence. */
export const SHUTTLE_STOP_MAX_FIX_GAP_MS = 25_000;

export const EMPTY_SHUTTLE_STOP_DWELL: ShuttleStopDwell = {
	enteredAtMs: null,
	lastFixAtMs: null,
	consecutiveFixes: 0,
};

/** A stop sighting is sound only when the complete reported accuracy circle fits inside. */
export function insideShuttleStop(fix: ShuttleStopFix): boolean {
	return (
		Number.isFinite(fix.accuracyMeters) &&
		fix.accuracyMeters >= 0 &&
		distanceMeters(fix, SHUTTLE_STOP) + fix.accuracyMeters <= SHUTTLE_STOP_RADIUS_METERS
	);
}

export function nextShuttleStopDwell(state: ShuttleStopDwell, fix: ShuttleStopFix): ShuttleStopDwell {
	if (state.lastFixAtMs != null && fix.fixAtMs <= state.lastFixAtMs) {
		return state;
	}
	if (!insideShuttleStop(fix)) {
		return { ...EMPTY_SHUTTLE_STOP_DWELL, lastFixAtMs: fix.fixAtMs };
	}
	if (state.lastFixAtMs == null || fix.fixAtMs - state.lastFixAtMs > SHUTTLE_STOP_MAX_FIX_GAP_MS) {
		return { enteredAtMs: fix.fixAtMs, lastFixAtMs: fix.fixAtMs, consecutiveFixes: 1 };
	}
	return {
		enteredAtMs: state.enteredAtMs ?? fix.fixAtMs,
		lastFixAtMs: fix.fixAtMs,
		consecutiveFixes: state.consecutiveFixes + 1,
	};
}

export function shuttleStopDwellReached(state: ShuttleStopDwell): boolean {
	return (
		state.enteredAtMs != null &&
		state.lastFixAtMs != null &&
		state.consecutiveFixes >= SHUTTLE_STOP_REQUIRED_FIXES &&
		state.lastFixAtMs - state.enteredAtMs >= SHUTTLE_STOP_DWELL_MS
	);
}